import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/cheap-seo-true-cost-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/cheap-seo-true-cost-colorado")({
  head: () => ({
    meta: [
      { title: "Cheap SEO: What It Really Costs Your Colorado Business (2026)" },
      { name: "description", content: "Cheap SEO looks like a deal until you see what it does to your rankings. What $99–$500/month actually buys, the damage it can do, and what real SEO costs in Colorado." },
      { property: "og:title", content: "Cheap SEO: What It Really Costs Your Colorado Business (2026)" },
      { property: "og:description", content: "What cheap SEO actually buys, the damage it can do, and what real SEO costs in Colorado." },
      { property: "og:url", content: `${SITE_URL}/learn/cheap-seo-true-cost-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Cheap SEO: the true cost",
        description: ARTICLE?.desc ?? "What cheap SEO actually buys and what real SEO costs in Colorado.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/cheap-seo-true-cost-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="9 min read"
      title="Cheap SEO: what it really costs your Colorado business"
      intro="Every week, Colorado business owners get emails promising page-one rankings for $99 a month. Some sign up. Then they call us a year later to undo the damage. Here's what cheap SEO actually buys, and what the real thing costs."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Cheap SEO" },
      ]}
      path="/learn/cheap-seo-true-cost-colorado"
      faqs={[
        { q: "Is cheap SEO ever worth it?", a: "At $99–$300/month you're buying software with a logo on it, or worse, link schemes that can get your site penalized. If the budget is truly tight, you're better off doing the basics yourself — claim your Google Business Profile, ask for reviews, fix your title tags — than paying for fake SEO." },
        { q: "Can cheap SEO get my site penalized?", a: "Yes. Bulk link buying and spun content are against Google's rules. We've taken over Colorado sites that lost most of their traffic after a cheap provider built thousands of junk links. Recovery takes months and costs more than doing it right would have." },
        { q: "What does real SEO cost in Colorado?", a: "Real local SEO runs $950 to $2,750 per month depending on how many cities and services you target. Our plans are published: Starter $950/mo, Growth $1,650/mo, Expert $2,750/mo — month to month, no long contracts." },
      ]}
    >
      <h2>What $99–$500 a month actually buys</h2>
      <p>
        SEO is labor: researching what your customers search, writing pages that answer those
        searches, fixing your site's technical problems, and earning links. At $99/month, nobody
        is doing that labor. What you're usually getting:
      </p>
      <ul>
        <li><strong>An automated report.</strong> Software scans your site and emails you a PDF. Nothing changes on your site.</li>
        <li><strong>Directory submissions.</strong> Your business gets blasted to hundreds of junk directories — worthless or harmful.</li>
        <li><strong>Spun content.</strong> Near-duplicate &ldquo;articles&rdquo; posted to your blog that Google ignores or penalizes.</li>
        <li><strong>Bulk links.</strong> Thousands of links from foreign link farms. This is the one that can get you penalized.</li>
      </ul>

      <h2>The expensive part comes later</h2>
      <p>
        The real cost of cheap SEO isn't the monthly fee — it's the cleanup. Removing a link
        penalty means auditing thousands of backlinks, disavowing the junk, and rebuilding
        Google's trust. We've moved companies from pages 6–10 back to page one after exactly this
        situation, and it takes far longer than starting clean. Our{" "}
        <Link to="/learn/page-10-to-page-1-seo-colorado">page 10 to page 1 guide</Link> walks
        through what that recovery looks like.
      </p>

      <h2>If the budget is tight, do this instead</h2>
      <p>
        A single-location Colorado business can get real traction with free fundamentals: claim
        and complete your <Link to="/learn/google-business-profile">Google Business Profile</Link>,
        ask every happy customer for a review, make sure your site names your services and cities
        in plain words, and keep your name, address, and phone identical everywhere. That beats
        $99/month &ldquo;SEO&rdquo; every time.
      </p>

      <h2>What real SEO costs</h2>
      <p>
        Real local SEO in Colorado starts around <strong>$950/month</strong> for one market,
        <strong> $1,650/month</strong> when you add AI search visibility and social, and{" "}
        <strong>$2,750/month</strong> for multi-city campaigns with ads and reputation management.
        Every tier is published on our <Link to="/pricing">pricing page</Link> — and month to
        month, because if it's working you won't want to leave.
      </p>

      <h2>What to do next</h2>
      <p>
        Get a <Link to="/free-audit">free digital marketing audit</Link> — we'll show you what
        your site actually needs before you spend a dollar, or read{" "}
        <Link to="/learn/colorado-seo-services">what Colorado SEO services should include</Link>.
      </p>
    </ArticleLayout>
  );
}
